import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { useI18n } from "../i18n/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { lang, t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      setStatus("success");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.response?.data?.detail || err.message || "Error");
      setStatus("error");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="cs-overline mb-4">{t("contact.overline")}</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight mb-6">
            {t("contact.title")}
          </h2>
          <p className="text-slate-400 leading-relaxed mb-10">{t("contact.subtitle")}</p>

          <ul className="space-y-5">
            <li className="flex items-start gap-4">
              <span className="h-10 w-10 rounded-lg bg-sky-400/10 border border-sky-400/30 flex items-center justify-center">
                <Mail className="w-4 h-4 text-sky-400" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{t("contact.info.email_label")}</p>
                <a href="mailto:info@cloud-ship.de" className="text-white hover:text-sky-400 block" data-testid="contact-info-email">
                  info@cloud-ship.de
                </a>
                <a href="mailto:samer.alshreibati@cloud-ship.de" className="text-slate-400 hover:text-sky-400 block text-sm mt-0.5" data-testid="contact-info-email-direct">
                  samer.alshreibati@cloud-ship.de
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="h-10 w-10 rounded-lg bg-sky-400/10 border border-sky-400/30 flex items-center justify-center">
                <Phone className="w-4 h-4 text-sky-400" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{t("contact.info.phone_label")}</p>
                <a href="tel:+4915732521421" className="text-white hover:text-sky-400" data-testid="contact-info-phone">
                  +49 157 32521421
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="h-10 w-10 rounded-lg bg-sky-400/10 border border-sky-400/30 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-sky-400" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{t("contact.info.location_label")}</p>
                <p className="text-white">{t("contact.info.location_value")}</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="h-10 w-10 rounded-lg bg-sky-400/10 border border-sky-400/30 flex items-center justify-center">
                <Clock className="w-4 h-4 text-sky-400" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{t("contact.info.hours_label")}</p>
                <p className="text-white">{t("contact.info.hours_value")}</p>
              </div>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            className="cs-glass rounded-2xl p-7 lg:p-10 space-y-5"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block">
                  {t("contact.form.name")}
                </label>
                <input
                  required
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  className="cs-input"
                  data-testid="contact-form-name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block">
                  {t("contact.form.email")}
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  className="cs-input"
                  data-testid="contact-form-email"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block">
                {t("contact.form.company")}
              </label>
              <input
                name="company"
                value={form.company}
                onChange={onChange}
                className="cs-input"
                data-testid="contact-form-company"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block">
                {t("contact.form.message")}
              </label>
              <textarea
                required
                name="message"
                rows={6}
                value={form.message}
                onChange={onChange}
                className="cs-input resize-none"
                data-testid="contact-form-message"
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <button
                type="submit"
                disabled={status === "sending"}
                className="cs-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="contact-form-submit"
              >
                {status === "sending" ? t("contact.form.sending") : t("contact.form.submit")}
                <Send className="w-4 h-4" />
              </button>

              {status === "success" && (
                <div
                  className="flex items-center gap-2 text-sm text-sky-400"
                  data-testid="contact-form-success"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{t("contact.form.success")}</span>
                </div>
              )}
              {status === "error" && (
                <div
                  className="flex items-center gap-2 text-sm text-rose-400"
                  data-testid="contact-form-error"
                >
                  <AlertTriangle className="w-4 h-4" />
                  <span>{t("contact.form.error")}</span>
                </div>
              )}
            </div>
            {status === "error" && errorMsg && (
              <p className="text-xs text-rose-400/80">{errorMsg}</p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
